import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import {
  listSchlageAccessCodes,
  mintSchlageAccessCode,
  revokeSchlageAccessCode,
  SchlageAccessCode,
} from '../../api/schlageApi';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Intrlock device row — must have acs_system_id + acs_device_id + name */
  device: { id: string; name: string; acs_system_id: string; acs_device_id: string };
}

// Schlage PIN constraints (from developer.allegion.com/.../getting_started_res_access_codes):
//   code   = 4-8 digits
//   name   = 1-12 alphanumeric characters
const CODE_RE = /^\d{4,8}$/;
const NAME_RE = /^[a-zA-Z0-9 ]{1,12}$/;

export const SchlagePinManager: React.FC<Props> = ({ open, onClose, device }) => {
  const [codes, setCodes] = useState<SchlageAccessCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);

  // New-code form
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [scheduleType, setScheduleType] = useState<'Always' | 'Temporary'>('Always');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listSchlageAccessCodes(device.acs_system_id, device.acs_device_id);
      setCodes(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      refresh();
      setAdding(false);
      setNewName(''); setNewCode(''); setScheduleType('Always');
      setStartDate(''); setEndDate('');
    }
  }, [open, device.acs_device_id]);

  const submitNew = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!NAME_RE.test(newName.trim())) { setError('Name: 1-12 alphanumeric characters'); return; }
    if (!CODE_RE.test(newCode.trim())) { setError('Code: 4-8 digits'); return; }
    let schedule: any = { ScheduleType: 'Always' };
    if (scheduleType === 'Temporary') {
      if (!startDate || !endDate) { setError('Temporary codes need start and end dates'); return; }
      schedule = {
        ScheduleType: 'Temporary',
        StartTime: new Date(startDate).toISOString(),
        EndTime: new Date(endDate).toISOString(),
      };
    }
    setSaving(true);
    try {
      await mintSchlageAccessCode(device.acs_system_id, device.acs_device_id, newCode.trim(), newName.trim(), schedule);
      await refresh();
      setAdding(false);
      setNewName(''); setNewCode('');
    } catch (e: any) {
      setError(e?.message || 'Failed to add code');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (codeId: string, label: string) => {
    if (!window.confirm(`Revoke PIN "${label}"? The user will no longer be able to enter the lock with this code.`)) return;
    setError('');
    try {
      await revokeSchlageAccessCode(device.acs_system_id, device.acs_device_id, codeId);
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Failed to revoke');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`PIN Codes — ${device.name}`} size="lg">
      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-gray-600">Manage residential access codes on this Schlage lock.</p>
        {!adding && (
          <Button onClick={() => setAdding(true)}>+ Add PIN</Button>
        )}
      </div>

      {adding && (
        <form onSubmit={submitNew} className="mb-4 p-4 border border-gray-200 rounded-lg space-y-3">
          <Input
            label="Name (1-12 letters/digits)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Cleaner1"
            required
          />
          <Input
            label="Code (4-8 digits)"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="e.g. 1234"
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
            <select
              value={scheduleType}
              onChange={(e) => setScheduleType(e.target.value as 'Always' | 'Temporary')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="Always">Always valid</option>
              <option value="Temporary">Temporary (date range)</option>
            </select>
          </div>
          {scheduleType === 'Temporary' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setAdding(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>Add PIN</Button>
          </div>
        </form>
      )}

      {loading && <p className="text-sm text-gray-400 text-center py-6">Loading codes…</p>}
      {!loading && codes.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-6">No PIN codes on this lock.</p>
      )}
      {!loading && codes.length > 0 && (
        <div className="border border-gray-100 rounded-lg divide-y">
          {codes.map((c, i) => {
            const id = String(c.AccessCodeId ?? c.accessCodeId ?? i);
            const name = String(c.Name ?? '—');
            const code = String(c.AccessCode ?? '••••');
            const sched = (c.Schedule as any)?.ScheduleType ?? 'Always';
            return (
              <div key={id} className="flex items-center justify-between p-3">
                <div>
                  <div className="font-medium text-gray-900 text-sm">{name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    <span className="font-mono">{code}</span> · <Badge color="gray">{sched}</Badge>
                  </div>
                </div>
                <button
                  onClick={() => revoke(id, name)}
                  className="text-xs text-red-600 hover:underline font-medium"
                >
                  Revoke
                </button>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};
